import { useQueryClient } from '@tanstack/react-query';
import { Modal, notification } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';
import { useDeleteContractPerson } from '@/features/contracts/api/use-delete-contract-person';
import { CONTRACT_MODE, useContractModalStore } from '@/store/use-contract-modal-store';

interface DeleteAttorneyModalProps {
  isModalOpen: boolean;
  contractPersonId: number;
}

const DeleteAttorneyModal = ({ isModalOpen, contractPersonId }: DeleteAttorneyModalProps) => {
  const { t } = useTranslation('contractAttorneyModal');

  const queryClient = useQueryClient();
  const { setMode } = useContractModalStore();

  const deleteContractPersonMutation = useDeleteContractPerson({
    mutationConfig: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes('contract-persons'),
        });
        notification.success({
          message: t('dataDeleted', { ns: 'common' }),
        });
        setMode(CONTRACT_MODE.MANAGE_ATTORNEYS);
      },
      onError: () => {
        notification.error({
          message: t('somethingWentWrong', { ns: 'common' }),
        });
      },
    },
  });

  const handleDeleteContractPerson = async () => {
    deleteContractPersonMutation.mutate(
      {
        personId: contractPersonId,
      },
      {
        onError: () => {
          notification.error({
            message: t('somethingWentWrong', { ns: 'common' }),
          });
        },
      },
    );
  };

  const handleClose = () => {
    setMode(CONTRACT_MODE.MANAGE_ATTORNEYS);
  };

  return (
    <Modal
      centered
      open={isModalOpen}
      onCancel={handleClose}
      onClose={handleClose}
      styles={{
        content: {
          width: '100%',
          justifySelf: 'center',
          borderRadius: 24,
        },
      }}
      title={
        <div className="flex gap-4 items-center text-[24px] text-bold">
          <span className="text-gray-10">{t('sureToDelete')}</span>
        </div>
      }
      footer={
        <div className="gap-4 flex">
          <Button
            pending={deleteContractPersonMutation.isPending}
            disabled={deleteContractPersonMutation.isPending}
            onClick={handleClose}
            variant="secondaryAlt"
            className="outline-none text-white flex-1 h-12 bg-[#B9B9B9] rounded-[14px] flex justify-center items-center"
          >
            {t('no', { ns: 'common' })}
          </Button>
          <Button
            pending={deleteContractPersonMutation.isPending}
            disabled={deleteContractPersonMutation.isPending}
            onClick={handleDeleteContractPerson}
            variant="warning"
            className="outline-none text-gray-10 flex-1 h-12 rounded-[14px] flex justify-center items-center bg-[#F97315] text-white"
          >
            {t('yes', { ns: 'common' })}
          </Button>
        </div>
      }
    ></Modal>
  );
};

export default DeleteAttorneyModal;
